// src/store/modules/wlb.js
import WlbProfilesService from '@/services/WlbService'

const state = () => ({
  loading: false,
  saving: false,
  error: null,

  profiles: [],
  currentId: null,
  current: null,

  // normalized directory for UI
  directory: []
})

const getters = {
  loading: (s) => s.loading,
  saving: (s) => s.saving,
  error: (s) => s.error,

  profiles: (s) => s.profiles,
  directory: (s) => s.directory,

  currentProfileId: (s) => s.currentId,
  currentProfile: (s) => s.current,

  profileById: (s) => (id) => s.profiles.find(p => String(p.id) === String(id))
}

const mutations = {
  SET_LOADING(state, v) { state.loading = !!v },
  SET_SAVING(state, v) { state.saving = !!v },
  SET_ERROR(state, e) { state.error = e || null },

  SET_PROFILES(state, profiles) {
    state.profiles = Array.isArray(profiles) ? profiles : []
  },

  UPSERT_PROFILE_IN_LIST(state, profile) {
    if (!profile?.id) return
    const id = String(profile.id)
    const idx = state.profiles.findIndex(p => String(p.id) === id)
    if (idx >= 0) state.profiles.splice(idx, 1, profile)
    else state.profiles.unshift(profile)
  },

  REMOVE_PROFILE_FROM_LIST(state, id) {
    const sid = String(id)
    state.profiles = state.profiles.filter(p => String(p.id) !== sid)
  },

  SET_CURRENT_ID(state, id) { state.currentId = id == null ? null : String(id) },
  SET_CURRENT(state, profile) { state.current = profile || null },

  SET_DIRECTORY(state, dir) {
    state.directory = Array.isArray(dir) ? dir : []
  }
}

/**
 * Unwrap helper:
 * - /users returns a paginator with top-level `data: [...]`
 * - some endpoints return { data: { data: [...] } }
 */
function unwrap(resp) {
  if (!resp) return null
  const d = resp.data
  if (!d) return null

  // Laravel paginator: { current_page, data: [...] }
  if (d && typeof d === 'object' && Array.isArray(d.data)) return d.data

  // Laravel resource: { data: [...] } or { data: { ... } }
  if (d && typeof d === 'object' && 'data' in d) return d.data

  return d
}

function normalizeDirectoryUser(u) {
  // ext: prefer `extension`, fallback to primary sip_users[0].ext
  const primarySip = Array.isArray(u.sip_users) ? u.sip_users.find(s => s.is_primary) : null
  const ext = u.extension ?? primarySip?.ext ?? null

  // title: we can show a decent label from customer_roles if you want
  // (or keep null if you prefer)
  const title =
    Array.isArray(u.customer_roles) && u.customer_roles.length
      ? u.customer_roles[0] // show first role (e.g. PBX Admin)
      : null

  return {
    id: String(u.id),
    name: u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim(),
    ext: ext ? String(ext) : null,
    title
  }
}

function normalizeListRow(p) {
  if (!p) return p
  return {
    ...p,
    actions: p.actions || { listen: true, whisper: false, barge: false },
    monitors: Array.isArray(p.monitors) ? p.monitors : Array(p.monitors_count || 0).fill({}),
    targets: Array.isArray(p.targets) ? p.targets : Array(p.targets_count || 0).fill({})
  }
}

const actions = {
  async bootstrap({ dispatch }) {
    await Promise.all([dispatch('fetchProfiles'), dispatch('fetchDirectory')])
  },

  async fetchDirectory({ commit }) {
    commit('SET_ERROR', null)
    try {
      // Optionally: you can pass params that your /users endpoint supports
      // e.g. { per_page: 200 } etc.
      const res = await WlbProfilesService.directory({ per_page: 500 })
      const rows = unwrap(res) || []
      const normalized = (Array.isArray(rows) ? rows : []).map(normalizeDirectoryUser)
      commit('SET_DIRECTORY', normalized)
      return normalized
    } catch (e) {
      commit('SET_ERROR', e)
      throw e
    }
  },

  async fetchProfiles({ commit }) {
    commit('SET_ERROR', null)
    commit('SET_LOADING', true)
    try {
      const res = await WlbProfilesService.list()
      const rows = unwrap(res) || []
      const normalized = (Array.isArray(rows) ? rows : []).map(normalizeListRow)
      commit('SET_PROFILES', normalized)
      return normalized
    } catch (e) {
      commit('SET_ERROR', e)
      throw e
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async selectProfile({ commit }, id) {
    commit('SET_ERROR', null)
    commit('SET_LOADING', true)
    try {
      const res = await WlbProfilesService.get(id)
      const profile = unwrap(res)
      commit('SET_CURRENT_ID', id)
      commit('SET_CURRENT', profile)

      if (profile?.id) {
        commit('UPSERT_PROFILE_IN_LIST', normalizeListRow({
          id: profile.id,
          name: profile.name,
          active: profile.active,
          actions: profile.actions,
          monitors: profile.monitors || [],
          targets: profile.targets || [],
          updatedAt: profile.updatedAt
        }))
      }

      return profile
    } catch (e) {
      commit('SET_ERROR', e)
      throw e
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async createProfile({ dispatch }, payload) {
    const res = await WlbProfilesService.create(payload || {})
    const created = unwrap(res)
    await dispatch('fetchProfiles')
    if (created?.id) await dispatch('selectProfile', created.id)
    return created
  },

  async saveProfile({ commit, dispatch }, form) {
    commit('SET_ERROR', null)
    commit('SET_SAVING', true)
    try {
      const payload = {
        name: form?.name,
        active: !!form?.active,
        actions: form?.actions
      }

      if (form?.id) {
        await WlbProfilesService.update(form.id, payload)
        await dispatch('selectProfile', form.id)
        return { id: form.id }
      }

      const res = await WlbProfilesService.create(payload)
      const created = unwrap(res)
      await dispatch('fetchProfiles')
      if (created?.id) await dispatch('selectProfile', created.id)
      return created
    } catch (e) {
      commit('SET_ERROR', e)
      throw e
    } finally {
      commit('SET_SAVING', false)
    }
  },

  async deleteProfile({ commit, dispatch, state }, id) {
    commit('SET_ERROR', null)
    await WlbProfilesService.destroy(id)
    commit('REMOVE_PROFILE_FROM_LIST', id)

    if (String(state.currentId) === String(id)) {
      commit('SET_CURRENT_ID', null)
      commit('SET_CURRENT', null)
    }

    await dispatch('fetchProfiles')
  },

  async addMember({ dispatch, state }, { profileId, userId, role }) {
    await WlbProfilesService.addMember(profileId, { user_id: userId, role })
    if (String(state.currentId) === String(profileId)) await dispatch('selectProfile', profileId)
    else await dispatch('fetchProfiles')
  },

  async removeMember({ dispatch, state }, { profileId, userId, role }) {
    await WlbProfilesService.removeMember(profileId, { user_id: userId, role })
    if (String(state.currentId) === String(profileId)) await dispatch('selectProfile', profileId)
    else await dispatch('fetchProfiles')
  },

  async bulkAdd({ dispatch, state }, { profileId, role, userIds }) {
    await WlbProfilesService.bulkAdd(profileId, { role, user_ids: userIds })
    if (String(state.currentId) === String(profileId)) await dispatch('selectProfile', profileId)
    else await dispatch('fetchProfiles')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
